/**
 * Configuration for membership form fields
 * Defines form field structures for both short and full membership forms
 */

export interface FormField {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  errorMessage?: string;
  colSpan?: string;
  autoComplete?: string;
  options?: Array<{ id: string; label: string }>;
  description?: string;
}

export const getShortFormFields = (): FormField[] => [
  {
    id: "firstName",
    label: "Voornaam*",
    type: "text",
    required: true,
    errorMessage: "Voornaam is verplicht in te vullen",
    colSpan: "md:col-span-6",
  },
  {
    id: "lastName",
    label: "Achternaam*",
    type: "text",
    required: true,
    errorMessage: "Achternaam is verplicht in te vullen",
    colSpan: "md:col-span-6",
  },
  {
    id: "email",
    label: "E-mail*",
    type: "email",
    required: true,
    errorMessage: "E-mail is verplicht in te vullen",
    autoComplete: "email",
    colSpan: "md:col-span-12", // Full width for short form
  },
  {
    id: "ageConfirmation",
    label: "Ja, ik ben 18 jaar of ouder*",
    type: "checkbox",
    required: true,
    errorMessage: "Je moet 18 jaar of ouder zijn om lid te worden.",
  },
  {
    id: "terms",
    label:
      "Ja, ik ga akkoord met het <a href='/privacy-statement' class='underline' target='_blank' rel='noopener noreferrer'>privacy statement</a> en de <a href='https://nsc-multisite.ams3.digitaloceanspaces.com/Huishoudelijk_reglement_definitieve_versie_9525719f46.pdf' class='underline' target='_blank' rel='noopener noreferrer'>huishoudelijk regelement NSC</a>.*",
    type: "checkbox",
    required: true,
    errorMessage:
      "Om dit formulier in te vullen, moet je akkoord gaan met het privacy statement en de huishoudelijk regelement NSC",
  },
  {
    id: "newsletter",
    label: "Ja, ik meld mij aan voor de nieuwsbrief en blijf zo op de hoogte van nieuws en evenementen",
    type: "checkbox",
  },
];

export const getFullFormFields = (provinces: any[]): FormField[] => [
  {
    id: "firstName",
    label: "Voornaam*",
    type: "text",
    required: true,
    errorMessage: "Voornaam is verplicht in te vullen",
    colSpan: "md:col-span-6",
  },
  {
    id: "lastName",
    label: "Achternaam*",
    type: "text",
    required: true,
    errorMessage: "Achternaam is verplicht in te vullen",
    colSpan: "md:col-span-6",
  },
  {
    id: "email",
    label: "E-mail*",
    type: "email",
    required: true,
    errorMessage: "E-mail is verplicht in te vullen",
    autoComplete: "email",
    colSpan: "md:col-span-6",
  },
  {
    id: "gender",
    label: "Geslacht*",
    type: "select",
    required: true,
    errorMessage: "Geslacht is verplicht in te vullen",
    options: [
      {
        id: "female",
        label: "Vrouw",
      },
      {
        id: "male",
        label: "Man",
      },
      {
        id: "non_binary",
        label: "Non binair",
      },
      {
        id: "prefer_not_to_say",
        label: "Zeg ik liever niet",
      },
      {
        id: "unknown",
        label: "Onbekend",
      },
    ],
    colSpan: "md:col-span-6",
  },
  {
    id: "birthdate",
    label: "Geboortedatum*",
    type: "date",
    required: true,
    errorMessage: "Geboortedatum is verplicht in te vullen",
  },
  {
    id: "postalCode",
    label: "Postcode*",
    type: "text",
    required: true,
    errorMessage: "Postcode is verplicht in te vullen",
    colSpan: "md:col-span-8",
  },
  {
    id: "houseNumber",
    label: "Huisnummer*",
    type: "text",
    required: true,
    errorMessage: "Huisnummer is verplicht in te vullen",
    colSpan: "md:col-span-4",
  },
  {
    id: "street",
    label: "Straat*",
    type: "text",
    required: true,
    errorMessage: "Straat is verplicht in te vullen",
  },
  {
    id: "city",
    label: "Woonplaats*",
    type: "text",
    required: true,
    errorMessage: "Woonplaats is verplicht in te vullen",
  },
  {
    id: "municipality",
    label: "Gemeente*",
    type: "text",
    required: true,
    errorMessage: "Gemeente is verplicht in te vullen",
  },
  {
    id: "province",
    label: "Provincie*",
    type: "select",
    required: true,
    errorMessage: "Provincie is verplicht in te vullen",
    options: provinces ? provinces.map((province: any) => ({ id: province.id, label: province.name })) : [],
  },
  {
    id: "mobilePhone",
    label: "Mobiel telefoonnummer*",
    type: "tel",
    required: true,
    errorMessage: "Mobiel telefoonnummer is verplicht in te vullen",
  },
  {
    id: "phoneNumber",
    label: "Vast telefoonnummer",
    type: "tel",
    errorMessage: "Vast telefoonnummer is verplicht in te vullen",
  },
  {
    id: "membershipType",
    label: "Lidmaatschap",
    type: "radio",
    required: true,
    options: [
      {
        id: "nsc",
        label: "Lidmaatschap NSC (€24,- per jaar)",
      },
      {
        id: "combo",
        label: "Combilidmaatschap NSC en JSC (€24,- per jaar)",
      },
      {
        id: "jsc",
        label: "Lidmaatschap JSC (€5,- per jaar)",
      },
    ],
    description: "Combi- en JSC lidmaatschap zijn alleen beschikbaar voor leden tussen 14 en 30 jaar.",
    errorMessage: "Lidmaatschap is verplicht in te vullen",
  },
  {
    id: "terms",
    label:
      "Ja, ik ga akkoord met het <a href='/privacy-statement' class='underline' target='_blank' rel='noopener noreferrer'>privacy statement</a> en de <a href='https://nsc-multisite.ams3.digitaloceanspaces.com/Huishoudelijk_reglement_definitieve_versie_9525719f46.pdf' class='underline' target='_blank' rel='noopener noreferrer'>huishoudelijk regelement NSC</a>.*",
    type: "checkbox",
    required: true,
    errorMessage:
      "Om dit formulier in te vullen, moet je akkoord gaan met het privacy statement en de huishoudelijk regelement NSC",
  },
  {
    id: "contact",
    label: "Ik stem er mee in dat ik gecontacteerd wordt over activiteiten voor leden.*",
    type: "checkbox",
    required: true,
    errorMessage:
      "Om dit formulier in te vullen, moet je akkoord gaan met dat je gecontacteerd wordt over activiteiten voor leden.",
  },
  {
    id: "newsletter",
    label: "Ja, ik meld mij aan voor de nieuwsbrief en blijf zo op de hoogte van nieuws en evenementen",
    type: "checkbox",
  },
]; 